import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { ArrowLeft, FilePdf, Search } from 'react-bootstrap-icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { listStoreDocuments } from '../../../services/storeDocumentsApi';
import { resolveFileFromCandidates } from '../../../utils/fileUrl';
import '../../../assets/styles/files.css';

const StoreFilesFormView = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { folderId } = useParams();
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchNotes, setSearchNotes] = useState('');

  const selectedFormName = useMemo(() => {
    if (state?.formName) return state.formName;
    try { return decodeURIComponent(folderId); } catch { return folderId; }
  }, [folderId, state?.formName]);

  const normalizeDocumentGroups = useCallback((data = []) => {
    if (!Array.isArray(data)) return [];
    return data.map((group) => ({
      ...group,
      documents: Array.isArray(group.documents) ? group.documents : [],
    }));
  }, []);

  const fetchDocuments = useCallback(async () => {
    if (!selectedFormName) return;

    try {
      setIsLoading(true);
      setError('');
      const params = {};
      if (searchNotes.trim()) params.notes = searchNotes.trim();
      const { data } = await listStoreDocuments(params);
      const groups = normalizeDocumentGroups(data);
      const matchingGroup = groups.find(
        (group) => String(group.fileNameId) === folderId || group.formName === selectedFormName,
      );
      setDocuments(matchingGroup ? matchingGroup.documents : []);
    } catch (err) {
      setError(err.message || 'Failed to load documents.');
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [folderId, normalizeDocumentGroups, searchNotes, selectedFormName]);

  useEffect(() => {
    const timeout = setTimeout(() => { fetchDocuments(); }, 300);
    return () => clearTimeout(timeout);
  }, [fetchDocuments]);

  return (
    <div className="files-page container-fluid py-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <button type="button" className="btn btn-link text-decoration-none ps-0" onClick={() => navigate('/store/files')}>
            <ArrowLeft className="me-2" /> Back to folders
          </button>
          <h4 className="mb-0 fw-semibold">{selectedFormName || 'Documents'}</h4>
        </div>
      </div>

      <div className="files-filters shadow-sm mb-4">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label text-muted small fw-semibold text-uppercase">Search Notes</label>
            <div className="files-search-control">
              <Search className="search-icon" size={18} />
              <input type="text" className="form-control files-input" placeholder="Search Notes" value={searchNotes} onChange={(e) => setSearchNotes(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      <div className="card files-table-card shadow-sm">
        <div className="card-body">
          {isLoading ? (
            <div className="py-5 text-center"><Spinner animation="border" role="status" /></div>
          ) : error ? (
            <div className="py-5 px-3"><Alert variant="danger" className="mb-0">{error}</Alert></div>
          ) : documents.length === 0 ? (
            <div className="files-empty-state text-center py-5">
              <div className="files-empty-icon d-inline-flex align-items-center justify-content-center mb-3"><FilePdf size={28} /></div>
              <p className="mb-1 fw-semibold">No documents found.</p>
              <small className="text-muted">Try adjusting filters.</small>
            </div>
          ) : (
            <div className="files-folder-body">
              {documents.map((document) => {
                const fileUrl = resolveFileFromCandidates(document.fileUrl, document.filePath);
                return (
                  <div className="files-folder-file" key={document.id}>
                    <div className="files-file-main">
                      <div className="files-file-icon"><FilePdf size={20} /></div>
                      <div className="files-file-info"><p className="files-file-name mb-1">{document.originalName || 'Document'}</p><p className="files-file-notes text-muted mb-0">{document.notes || 'No notes provided'}</p></div>
                      <div className="files-file-actions d-flex align-items-center gap-2">{fileUrl ? <a href={fileUrl} className="btn btn-icon" target="_blank" rel="noopener noreferrer" aria-label="View document"><FilePdf size={18} /></a> : <span className="text-muted small">N/A</span>}</div>
                    </div>
                    <div className="files-file-footer d-flex flex-wrap gap-3"><span className="text-muted small">Outlet: {document.outlet?.name || document.outlet?.branchName || '-'}</span><span className="text-muted small">Uploaded: {document.createdAt ? new Date(document.createdAt).toLocaleString() : '-'}</span>{document.uploader?.name && <span className="text-muted small">By {document.uploader.name}</span>}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreFilesFormView;
